import {AxiosError} from 'axios';
import {useState} from 'react';
import {useNavigate} from 'react-router-dom';
import Button from '../../../../../components/button';
import DotsLoading from '../../../../../components/dotsLoading';
import SpinnerLoading from '../../../../../components/SpinnerLoading';
import handleDownload from '../../../../../module/pdfFarmCreditReport/DownloadReport';
import useGetLoanValidationResult from '../../../../../services/hookService/getLoanValidationResult/useGetLoanValidationResult';
import usePatchDirectLoanRequest from '../../../../../services/hookService/patchDirectLoanRequest/usePatchDirectLoanRequest';
import usePatchLoanRequest from '../../../../../services/hookService/patchLoanRequest/usePatchLoanRequest';
import usePostFarmValidationFile from '../../../../../services/hookService/postFarmValidationFile/usePostFarmValidationFile';
import usePostLoanValidationRequest from '../../../../../services/hookService/postLoanValidationRequest/usePostLoanValidationRequest';
import UpgradeAccBtmSheet from '../status1/UpgradeAccBtmSheet';
import ValidationLetter from './ValidationLetter';
import ValidationScoreBoard from './ValidationScoreBoard';

const ValidationsResult = ({loanItem}: {loanItem: ILoanRequestItem}) => {
  const navigate = useNavigate();
  const [isOpenUpgradeAcc, setIsOpenUpgradeAcc] = useState<boolean>(false);
  const [isPdfGenerating, setIsPdfGenerating] = useState<boolean>(false);

  const {data, isLoading} = useGetLoanValidationResult(loanItem?.guid);

  const {mutate: mutatePatchLoan, isLoading: isLoadingPatchLoan} =
    usePatchLoanRequest();

  const {mutate: mutatePatchDirectLoan, isLoading: isLoadingPatchDirectLoan} =
    usePatchDirectLoanRequest();

  const handleRevalidationRequest = () => {
    const payload: IPatchLoanRequestPayload | IPatchDirectLoanRequestPayload = {
      loan_request_guid: loanItem.guid,
      status: 1,
    };

    const onSuccess = () => navigate('/my-credits/startLoanCredit');

    loanItem.is_direct
      ? mutatePatchDirectLoan(payload, {onSuccess})
      : mutatePatchLoan(payload, {onSuccess});
  };

  const {mutate: mutateLoanValidation, isLoading: isLoadingLoanValidation} =
    usePostLoanValidationRequest();

  const {mutateAsync: uploadPdf} = usePostFarmValidationFile();

  const isLoadingRevalidation = isLoadingPatchLoan || isLoadingPatchDirectLoan;

  const handleSeeDetails = () => {
    mutateLoanValidation(loanItem.guid, {
      onSuccess: async () => {
        setIsPdfGenerating(true);
        await handleDownload({
          guid: loanItem.guid,
          uploadPdf,
        });
        setIsPdfGenerating(false);
      },
      onError: async error => {
        const axiosError = error as AxiosError<{message: string[]}>;
        const errorCode = axiosError.response?.status;

        if (errorCode === 400) {
          setIsPdfGenerating(true);
          await handleDownload({
            guid: loanItem?.guid,
            uploadPdf,
          });
          setIsPdfGenerating(false);
        }
        if (errorCode === 402) {
          setIsOpenUpgradeAcc(true);
        }
      },
    });
  };

  return (
    <div>
      <div className="space-y-2">
        <span className="text-gray-800 font-Medium text-md">
          اعتبارسنجی مزرعه
        </span>
        <div className="flex flex-col items-center justify-center gap-2 p-3 rounded-lg bg-gray-50">
          {isLoading && <SpinnerLoading />}

          {!isLoading && data && (
            <>
              <div className="flex flex-col items-center justify-center gap-3">
                <ValidationScoreBoard score={data.overall_score} />
                <ValidationLetter letter={data.loan_validation_letter} />
              </div>

              <span className="mt-1 text-sm font-medium text-gray-900">
                پیش بینی میزان ریسک فنی، مدیریتی، اقلیمی و بازاری{' '}
              </span>

              <div className="flex items-center justify-between w-full mt-3 text-md">
                <span className="text-gray-700">رتبه اعتباری</span>
                <span className="font-semibold ">
                  {data.loan_validation_letter}
                </span>
              </div>

              <div className="w-full h-[1px] bg-gray-300 my-2" />

              <span className="text-justify text-gray-700 text-md">
                نتیجه این ارزیابی بر اساس اطلاعات مزرعه، کشت های ثبت شده و داده
                های گردآوری شده از منابع گوناگون است.
              </span>

              <div className="flex items-center w-full gap-2 mt-2">
                <Button
                  className="w-full px-3 py-2 rounded-lg text-farm-shop-500 border-[1px] border-farm-shop-500 text-md"
                  variant="outline"
                  onClick={handleRevalidationRequest}
                  disabled={isLoadingRevalidation}
                >
                  {isLoadingRevalidation ? <DotsLoading /> : 'اعتبارسنجی مجدد'}
                </Button>
                <Button
                  className="w-full px-3 py-2 text-white rounded-lg bg-farm-shop-500 text-md"
                  onClick={handleSeeDetails}
                  isLoading={isLoadingLoanValidation || isPdfGenerating}
                >
                  مشاهده جزئیات
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
      <UpgradeAccBtmSheet
        isOpen={isOpenUpgradeAcc}
        setIsOpen={setIsOpenUpgradeAcc}
        loanGuid={loanItem?.guid!}
      />
    </div>
  );
};

export default ValidationsResult;
